import React, { useEffect, useState } from "react";
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Alert, Grid, Switch, Tabs, Tab, FormControlLabel, Checkbox, MenuItem,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import adminApi from "../api/adminApi";

// "Why Not offer" card of the website (BackendNode routes/offers.js). One offer is on at a time:
// its extra is free (or discounted) in every booking while it is on, and the card shows it.
const API_ROOT = "https://app.autocrescente.com/WhyNotApi/";
const LOCALES = [
  { code: "en", label: "English" },
  { code: "pt", label: "Português" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "pl", label: "Polski" },
];
const PLACEMENTS = [
  { code: "home", label: "Home page" },
  { code: "fleet", label: "Fleet page" },
];
const EXTRAS = [
  { code: "additionalDriver", label: "Additional driver" },
  { code: "babySeat", label: "Baby seat" },
  { code: "babySeatIsofix", label: "Baby seat (Isofix)" },
  { code: "boosterSeat", label: "Booster seat" },
  { code: "fullInsurance", label: "Full insurance" },
];
const LAYOUTS = [
  { code: "dark", label: "Dark" },
  { code: "orange", label: "Orange" },
  { code: "light", label: "Light" },
  { code: "image", label: "With image" },
];
const FIELDS = ["eyebrow", "title", "highlight", "text", "cta"];

// Suggested texts per language, from the extra and the benefit ("Fill texts").
const EXTRA_NAMES = {
  en: { additionalDriver: "Additional driver", babySeat: "Baby seat", babySeatIsofix: "Isofix baby seat", boosterSeat: "Booster seat", fullInsurance: "Full insurance" },
  pt: { additionalDriver: "Condutor adicional", babySeat: "Cadeira de bebé", babySeatIsofix: "Cadeira Isofix", boosterSeat: "Banco elevatório", fullInsurance: "Seguro completo" },
  es: { additionalDriver: "Conductor adicional", babySeat: "Silla de bebé", babySeatIsofix: "Silla Isofix", boosterSeat: "Elevador", fullInsurance: "Seguro completo" },
  de: { additionalDriver: "Zusatzfahrer", babySeat: "Babysitz", babySeatIsofix: "Isofix-Babysitz", boosterSeat: "Sitzerhöhung", fullInsurance: "Vollversicherung" },
  fr: { additionalDriver: "Conducteur additionnel", babySeat: "Siège bébé", babySeatIsofix: "Siège Isofix", boosterSeat: "Réhausseur", fullInsurance: "Assurance complète" },
  pl: { additionalDriver: "Dodatkowy kierowca", babySeat: "Fotelik dziecięcy", babySeatIsofix: "Fotelik Isofix", boosterSeat: "Podkładka", fullInsurance: "Pełne ubezpieczenie" },
};
const WORDS = {
  en: { eyebrow: "Why Not offer", free: "free", cta: "Book" },
  pt: { eyebrow: "Oferta Why Not", free: "grátis", cta: "Reservar" },
  es: { eyebrow: "Oferta Why Not", free: "gratis", cta: "Reservar" },
  de: { eyebrow: "Why Not Angebot", free: "gratis", cta: "Buchen" },
  fr: { eyebrow: "Offre Why Not", free: "offert", cta: "Réserver" },
  pl: { eyebrow: "Oferta Why Not", free: "gratis", cta: "Zarezerwuj" },
};

const NAVY = "#09233f";
const ORANGE = "#f47a20";
const ORANGE_DEEP = "#b4530a";
const LINE = "#dfe5eb";

const emptyContent = () =>
  Object.fromEntries(LOCALES.map((l) => [l.code, Object.fromEntries(FIELDS.map((f) => [f, ""]))]));
const dateInput = (d) => (d ? String(d).slice(0, 10) : "");
const errorText = (err, fallback) => err.response?.data?.message || fallback;
const extraLabel = (code) => EXTRAS.find((e) => e.code === code)?.label ?? code;
const benefitLabel = (o) => (o.benefit === "percent" ? `${o.percent}% off` : "free");

/** Colours of each layout, as the website draws them (new site src/components/PromoBanner.module.css). */
const LOOK = {
  dark: { bg: NAVY, color: "#fff", eyebrow: ORANGE, highlight: { color: ORANGE }, text: "rgba(255,255,255,.78)", cta: { bg: ORANGE, color: NAVY }, circle: ORANGE, border: "none" },
  orange: { bg: ORANGE, color: NAVY, eyebrow: NAVY, highlight: { color: NAVY, bg: "#fff" }, text: NAVY, cta: { bg: NAVY, color: "#fff" }, circle: "rgba(9,35,63,.12)", border: "none" },
  light: { bg: "#fff", color: NAVY, eyebrow: ORANGE_DEEP, highlight: { color: ORANGE_DEEP }, text: "#5b6b7b", cta: { bg: ORANGE, color: NAVY }, circle: "#fde9d9", border: `1px solid ${LINE}` },
  image: { bg: NAVY, color: "#fff", eyebrow: ORANGE, highlight: { color: ORANGE }, text: "rgba(255,255,255,.78)", cta: { bg: ORANGE, color: NAVY }, circle: null, border: "none" },
};

/** The card as the website draws it. `narrow` previews a phone. */
const OfferCardPreview = ({ content, layout, imageUrl, narrow }) => {
  const look = LOOK[layout] ?? LOOK.dark;
  const withImage = layout === "image";
  return (
    <Box
      sx={{
        position: "relative", overflow: "hidden", bgcolor: look.bg, color: look.color, border: look.border,
        borderRadius: "24px", maxWidth: narrow ? 340 : 470, boxShadow: "0 18px 45px rgba(7,27,48,.18)",
        display: "grid", gridTemplateColumns: withImage && !narrow ? "1fr 150px" : "1fr",
      }}
    >
      {withImage && narrow && (
        <Box sx={{ height: 130, bgcolor: "#1d3a57", backgroundImage: imageUrl ? `url(${imageUrl})` : "none", backgroundSize: "cover", backgroundPosition: "center" }} />
      )}
      <Box sx={{ position: "relative", p: narrow ? "20px" : "22px 26px" }}>
        {look.circle && (
          <Box sx={{ position: "absolute", width: 150, height: 150, borderRadius: "50%", bgcolor: look.circle, right: -85, bottom: -90 }} />
        )}
        {content.eyebrow && (
          <Typography sx={{ position: "relative", color: look.eyebrow, fontSize: 10, fontWeight: 800, letterSpacing: ".18em", textTransform: "uppercase", mb: 1 }}>
            {content.eyebrow}
          </Typography>
        )}
        <Box sx={{ position: "relative", display: "flex", flexDirection: narrow || withImage ? "column" : "row", alignItems: narrow || withImage ? "flex-start" : "center", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Typography component="strong" sx={{ display: "block", fontSize: narrow ? 28 : 34, fontWeight: 800, lineHeight: 1.05, letterSpacing: "-.03em" }}>
              {content.title}{" "}
              {content.highlight && (
                <span style={{ color: look.highlight.color, background: look.highlight.bg, padding: look.highlight.bg ? "0 .18em" : 0, borderRadius: 6 }}>
                  {content.highlight}
                </span>
              )}
            </Typography>
            {content.text && <Typography sx={{ mt: 1, fontSize: 13, color: look.text }}>{content.text}</Typography>}
          </Box>
          {content.cta && (
            <Box sx={{ bgcolor: look.cta.bg, color: look.cta.color, borderRadius: 999, px: 2, py: 1.25, fontSize: 10, fontWeight: 800, textTransform: "uppercase", whiteSpace: "nowrap", alignSelf: narrow ? "stretch" : "auto", textAlign: "center" }}>
              {content.cta} →
            </Box>
          )}
        </Box>
      </Box>
      {withImage && !narrow && (
        <Box sx={{ bgcolor: "#1d3a57", backgroundImage: imageUrl ? `url(${imageUrl})` : "none", backgroundSize: "cover", backgroundPosition: "center", minHeight: 150 }} />
      )}
    </Box>
  );
};

const NEW_OFFER = () => ({
  name: "", active: false, placements: ["home", "fleet"], startsAt: "", endsAt: "",
  extra: "babySeat", benefit: "free", percent: 50, units: 1, layout: "dark", image: "",
  content: emptyContent(),
});

const OfferDialog = ({ open, onClose, offer, onSaved }) => {
  const [form, setForm] = useState(null);
  const [locale, setLocale] = useState("en");
  const [device, setDevice] = useState("desktop");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(
      offer
        ? {
            ...NEW_OFFER(),
            ...offer,
            startsAt: dateInput(offer.startsAt),
            endsAt: dateInput(offer.endsAt),
            content: { ...emptyContent(), ...offer.content },
          }
        : NEW_OFFER()
    );
    setImageFile(null);
    setImagePreview(offer?.image ? `${API_ROOT}uploads/offers/${offer.image}` : "");
    setLocale("en");
    setError("");
  }, [offer, open]);

  if (!form) return null;
  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const setText = (field) => (e) =>
    setForm((p) => ({ ...p, content: { ...p.content, [locale]: { ...p.content[locale], [field]: e.target.value } } }));
  const togglePlacement = (code) =>
    setForm((p) => ({
      ...p,
      placements: p.placements.includes(code) ? p.placements.filter((c) => c !== code) : [...p.placements, code],
    }));

  // Suggested texts in every language from the extra and the benefit. Overwrites the texts.
  const fillTexts = () => {
    if (!window.confirm("Replace the texts in every language with the suggested ones?")) return;
    setForm((p) => ({
      ...p,
      content: Object.fromEntries(
        LOCALES.map(({ code }) => [
          code,
          {
            eyebrow: WORDS[code].eyebrow,
            title: EXTRA_NAMES[code][p.extra],
            highlight: p.benefit === "percent" ? `-${p.percent}%` : WORDS[code].free,
            text: "",
            cta: WORDS[code].cta,
          },
        ])
      ),
    }));
  };

  const onImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("The image must be JPG, PNG or WebP.");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setError("The image must be 3 MB or less.");
      return;
    }
    setError("");
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // Empty fields in another language show the English text on the site.
  const previewContent = Object.fromEntries(FIELDS.map((f) => [f, form.content[locale][f] || form.content.en[f]]));

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      const body = { ...form };
      delete body.image;
      const { data } = offer?._id
        ? await adminApi.put(`/offers/${offer._id}`, body)
        : await adminApi.post("/offers", body);
      if (imageFile) {
        const fd = new FormData();
        fd.append("image", imageFile);
        await adminApi.post(`/offers/${data._id}/image`, fd);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(errorText(err, "Save failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{offer?._id ? "Edit Why Not offer" : "New Why Not offer"}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="#1C2541" sx={{ mt: 1, mb: 1 }}>The offer</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}><TextField label="Name (admin only)" fullWidth value={form.name} onChange={set("name")} /></Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Extra offered" fullWidth value={form.extra} onChange={set("extra")}>
                  {EXTRAS.map((e) => <MenuItem key={e.code} value={e.code}>{e.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField select label="Benefit" fullWidth value={form.benefit} onChange={set("benefit")}>
                  <MenuItem value="free">Free</MenuItem>
                  <MenuItem value="percent">% off</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                {form.benefit === "percent" ? (
                  <TextField label="% off" type="number" fullWidth value={form.percent} onChange={set("percent")} inputProps={{ min: 1, max: 100 }} />
                ) : (
                  <TextField label="Units covered" type="number" fullWidth value={form.units} onChange={set("units")} inputProps={{ min: 1, max: 8 }} />
                )}
              </Grid>
              {form.benefit === "percent" && (
                <Grid item xs={12} sm={6}>
                  <TextField label="Units covered" type="number" fullWidth value={form.units} onChange={set("units")} inputProps={{ min: 1, max: 8 }} />
                </Grid>
              )}
              <Grid item xs={6} sm={6}><TextField label="Starts" type="date" fullWidth InputLabelProps={{ shrink: true }} value={form.startsAt} onChange={set("startsAt")} /></Grid>
              <Grid item xs={6} sm={6}><TextField label="Ends" type="date" fullWidth InputLabelProps={{ shrink: true }} value={form.endsAt} onChange={set("endsAt")} helperText="Last day of the offer" /></Grid>
              <Grid item xs={12} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                {PLACEMENTS.map((p) => (
                  <FormControlLabel key={p.code} label={p.label} control={<Checkbox checked={form.placements.includes(p.code)} onChange={() => togglePlacement(p.code)} />} />
                ))}
                <FormControlLabel label="On site" control={<Switch checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />} />
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 1 }}>
              While the offer is on, {form.benefit === "percent" ? `${form.percent}% comes off` : "the website does not charge"}{" "}
              {form.units > 1 ? `up to ${form.units} units of` : "one"} {extraLabel(form.extra).toLowerCase()} in every booking. Only one offer is on at a time.
            </Alert>

            <Typography variant="subtitle2" color="#1C2541" sx={{ mt: 3, mb: 1 }}>Layout</Typography>
            <ToggleButtonGroup exclusive size="small" value={form.layout} onChange={(e, v) => v && setForm((p) => ({ ...p, layout: v }))}>
              {LAYOUTS.map((l) => <ToggleButton key={l.code} value={l.code}>{l.label}</ToggleButton>)}
            </ToggleButtonGroup>
            {form.layout === "image" && (
              <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                <Button variant="outlined" component="label">
                  {imagePreview ? "Change image" : "Upload image"}
                  <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={onImage} />
                </Button>
                <Typography variant="caption" color="text.secondary">JPG, PNG or WebP, up to 3 MB. A photo of the extra works best.</Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 3 }}>
              <Typography variant="subtitle2" color="#1C2541">Texts</Typography>
              <Button size="small" startIcon={<AutoFixHighIcon />} onClick={fillTexts}>Fill texts from the extra</Button>
            </Box>
            <Tabs value={locale} onChange={(e, v) => setLocale(v)} variant="scrollable" sx={{ borderBottom: 1, borderColor: "divider" }}>
              {LOCALES.map((l) => <Tab key={l.code} value={l.code} label={l.code === "en" ? `${l.label} *` : l.label} />)}
            </Tabs>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {[
                ["eyebrow", "Small line (e.g. Why Not offer)", 6],
                ["cta", "Button (e.g. Book)", 6],
                ["title", "Big text (e.g. Baby seat)", 6],
                ["highlight", "Big text, highlighted (e.g. free)", 6],
                ["text", "Short line under it (optional)", 12],
              ].map(([field, label, md]) => (
                <Grid key={field} item xs={12} md={md}>
                  <TextField label={label} fullWidth value={form.content[locale][field]} onChange={setText(field)} placeholder={locale === "en" ? "" : form.content.en[field]} />
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ position: "sticky", top: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1, mb: 2 }}>
                <Typography variant="subtitle2" color="#1C2541">
                  Preview · {LOCALES.find((l) => l.code === locale).label}
                </Typography>
                <ToggleButtonGroup exclusive size="small" value={device} onChange={(e, v) => v && setDevice(v)}>
                  <ToggleButton value="desktop">Computer</ToggleButton>
                  <ToggleButton value="phone">Phone</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ p: 3, bgcolor: "#f7f9fb", borderRadius: 3, border: `1px solid ${LINE}` }}>
                <OfferCardPreview content={previewContent} layout={form.layout} imageUrl={imagePreview} narrow={device === "phone"} />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Empty fields use the English text. In the booking, the {extraLabel(form.extra).toLowerCase()} shows the offer next to its price.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading} sx={{ backgroundColor: "#1C2541" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const period = (from, to) => (from || to ? `${formatDate(from)} → ${formatDate(to)}` : "Always");

const OfferManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState({ open: false, item: null });

  const load = async () => {
    try {
      const { data } = await adminApi.get("/offers");
      setItems(data.items);
      setError("");
    } catch (err) {
      setError(errorText(err, "Could not load the offers"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (o) => {
    await adminApi.put(`/offers/${o._id}`, { active: !o.active });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    await adminApi.delete(`/offers/${id}`);
    load();
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="#1C2541" mb={1}>Why Not offer</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          The card on the home and fleet pages. The offer that is on makes its extra free (or cheaper) in every booking. Switching one on switches the others off. With none on, the site shows the Promotions banner or the default card.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, item: null })} sx={{ backgroundColor: "#1C2541" }}>
          New offer
        </Button>
      </Box>
      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f0f2f5" }}>
            <TableRow>
              <TableCell><strong>On site</strong></TableCell>
              <TableCell><strong>Offer</strong></TableCell>
              <TableCell><strong>Extra</strong></TableCell>
              <TableCell><strong>Layout</strong></TableCell>
              <TableCell><strong>Where</strong></TableCell>
              <TableCell><strong>Dates</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((o) => (
              <TableRow key={o._id} hover sx={{ opacity: o.active ? 1 : 0.65 }}>
                <TableCell><Switch checked={o.active} onChange={() => toggle(o)} /></TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">{o.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[o.content?.en?.title, o.content?.en?.highlight].filter(Boolean).join(" ")}
                  </Typography>
                </TableCell>
                <TableCell>{extraLabel(o.extra)} · {benefitLabel(o)}{o.units > 1 ? ` · ${o.units} units` : ""}</TableCell>
                <TableCell>{LAYOUTS.find((l) => l.code === o.layout)?.label}</TableCell>
                <TableCell>{(o.placements || []).map((c) => <Chip key={c} label={c} size="small" sx={{ mr: 0.5 }} />)}</TableCell>
                <TableCell>{period(o.startsAt, o.endsAt)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <IconButton size="small" onClick={() => setDialog({ open: true, item: o })}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => remove(o._id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!items.length && (
              <TableRow><TableCell colSpan={7} align="center">No offers yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <OfferDialog open={dialog.open} offer={dialog.item} onClose={() => setDialog({ open: false, item: null })} onSaved={load} />
    </Box>
  );
};

export default OfferManager;
